import React, { memo, useEffect } from "react";
import { Box } from "@chakra-ui/react";
import colors from "app/colors";
import { IClickedMention } from "modules/quill/use-quill";
import QuillClickedMention from "modules/quill/mentions/quill-clicked-mention";
import { usePubSub } from "modules/providers/pub-sub-provider";

interface IProps {
  containerRef: (node: HTMLDivElement | null) => void;
  focus: () => void;
  draftId: string;
  clickedMention?: IClickedMention | null;
  onClose: () => void;
  isExpanded?: boolean;
}

const QuillEditor = ({
  containerRef,
  focus,
  draftId,
  clickedMention,
  onClose,
  isExpanded = false,
}: IProps) => {
  const { subscribe, unsubscribe } = usePubSub();

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    // Chakra modals return focus to the previously focused element on close. Appears to be an issue even when returnFocusOnClose is set to false. Adding a slight delay seems to fix the issue.
    const handler = (targetDraftId: string) => {
      if (targetDraftId !== draftId) return;
      timeoutId = setTimeout(() => {
        focus();
      }, 100);
    };

    subscribe("focus-quill-editor", handler);
    return () => {
      unsubscribe("focus-quill-editor", handler);
      clearTimeout(timeoutId);
    };
  }, [draftId, focus, subscribe, unsubscribe]);

  return (
    <>
      <Box
        id="editor"
        w="full"
        flex={isExpanded ? 1 : undefined}
        minH={isExpanded ? 0 : "38px"}
        overflow={isExpanded ? "hidden" : undefined}
        py={2}
        className={isExpanded ? "quill-expanded" : "quill-collapsed"}
        _placeholder={{
          color: colors.placeholderText,
        }}
        ref={containerRef}
        sx={{
          ".ql-editor": {
            fontSize: "14px !important",
          },
        }}
      />
      <QuillClickedMention clickedMention={clickedMention} onClose={onClose} />
    </>
  );
};

export default memo(QuillEditor);
